<script setup lang="ts">
import { onMounted, ref, PropType } from 'vue';

type TurnstileSizeOptions = 'normal' | 'compact' | 'flexible';
type TurnstileTheme = 'light' | 'dark';

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: object) => void;
      reset: () => void;
    };
    onloadTurnstileCallback?: () => void;
  }
}

const props = defineProps({
  sitekey: {
    type: String,
    required: true,
  },
  size: {
    type: String as PropType<TurnstileSizeOptions>,
    default: 'normal',
  },
  theme: {
    type: String as PropType<TurnstileTheme>,
    default: 'light',
  },
});

const emit = defineEmits(['verify', 'expire', 'fail']);

// 3. Logic
const turnstileBox = ref<HTMLElement | null>(null);

const renderTurnstile = () => {
  if (window.turnstile && turnstileBox.value) {
    window.turnstile.render(turnstileBox.value, {
      sitekey: props.sitekey,
      theme: props.theme,
      size: props.size,
      callback: (token: string) => emit('verify', token),
      'expired-callback': () => emit('expire'),
      'error-callback': () => emit('fail'),
    });
  }
};

onMounted(() => {
  if (!window.turnstile) {
    window.onloadTurnstileCallback = renderTurnstile;

    if (!document.querySelector('script[src*="turnstile/v0/api.js"]')) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  } else {
    renderTurnstile();
  }
});

const reset = () => window.turnstile?.reset();
defineExpose({ reset });
</script>

<template>
  <div ref="turnstileBox" />
</template>
